import { useEffect, useMemo, useState } from "react";
import { StudentPaymentDetailPanelProps, StudentPaymentDetailType } from "../types";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Input,
  notification,
  Space,
  Spin,
  Table,
  Tag,
  Form,
  DatePicker,
  Modal,
  Radio,
  Tooltip,
  Select,
} from "antd";
import axiosInstance from "../../../axios";
import { ActionForNotification, NotificationType, popupTitleAndMessage } from "../../utils/notificationPopup";
import { DeleteOutlined, EditOutlined, LinkOutlined, RedoOutlined, SaveOutlined } from "@ant-design/icons";
import PaymentCommentsPopup from "../PaymentCommentsPopupProps";
import dayjs, { Dayjs } from "dayjs";
import { useAtom } from "jotai";
import { userRole as userRoleAtom, isTransferPaymentAccess as isTransferPaymentAccessAtom } from "../../../states/userRole";
import type { PrimitiveAtom } from "jotai";
import { USER_ROLES } from "../../../constants/roleConstants";
import { debounce } from "lodash";
export default function StudentPaymentDetail(props: StudentPaymentDetailPanelProps) {
  const [role] = useAtom(userRoleAtom as PrimitiveAtom<string>);
  const [isTransferPayment] = useAtom(isTransferPaymentAccessAtom as PrimitiveAtom<boolean>);
  const isEditable = role === USER_ROLES.ADMIN || role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUPERVISER;
  const isPaymentEditable = role === USER_ROLES.ADMIN || role === USER_ROLES.SUPERADMIN;
  const { studentDetails } = props;

  const [paymentDetails, setPaymentDetails] = useState<Partial<StudentPaymentDetailType>>({});
  const [customAmountRequest, setCustomAmountRequest] = useState(false);
  const [NotificationInstanceApi, NotificationInstanceContextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [isPaymentTypeModalOpen, setIsPaymentTypeModalOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<"basic" | "premium">("basic");
  const [ongoingTutorName, setOngoingTutorName] = useState("");
  const [editingTransferRowId, setEditingTransferRowId] = useState<number | null>(null);
  const [transferDraft, setTransferDraft] = useState<{
    tutor_transferred: boolean;
    transfer_date: Dayjs | null;
  }>({
    tutor_transferred: false,
    transfer_date: null,
  });
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferTutorData, setTransferTutorData] = useState<{
    tutor_id: number;
    fullname: string;
    email: string;
    phone: string;
    gender: string;
    qualification: string;
    profile_pic: string;
    bank_details: {
      accountHolderName?: string;
      accountNumber?: string;
      bankName?: string;
      ifscCode?: string;
      upiId?: string;
      panNumber?: string;
    };
  } | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferStep, setTransferStep] = useState<"loading" | "form">("loading");
  const [selectedPayment, setSelectedPayment] = useState<StudentPaymentDetailType["orders"][0] | null>(null);
  const [amountType, setAmountType] = useState<"percentage" | "fixed">("percentage");
  const [amountValue, setAmountValue] = useState<number>(0);
  const [calculatedAmount, setCalculatedAmount] = useState<number>(0);
  const [notes, setNotes] = useState("");
  const [razorpayLinkedAccountId, setRazorpayLinkedAccountId] = useState<string | null>(null);
  const [razorpayAccountStatus, setRazorpayAccountStatus] = useState<string | null>(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [raisingRequest, setRaisingRequest] = useState(false);
  const [isEditingBankDetails, setIsEditingBankDetails] = useState(false);
  const [bankDetailsDraft, setBankDetailsDraft] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    panNumber: "",
    upiId: "",
  });
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [transferTab, setTransferTab] = useState<"razorpay" | "manual">("razorpay");
  const [manualMode, setManualMode] = useState<string>("");
  const [manualRef, setManualRef] = useState<string>("");
  const [manualDate, setManualDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [manualNotes, setManualNotes] = useState<string>("");

  const getApiErrorMessage = (responseData: any, fallback = "Something went wrong") => {
    const errorPayload = responseData?.error;
    if (typeof errorPayload === "string" && errorPayload.trim()) {
      return errorPayload;
    }
    if (errorPayload?.message) {
      return errorPayload.message;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    return fallback;
  };

  useEffect(() => {
    if (!studentDetails?.id) return;
    fetchPaymentDetails();
    fetchOngoingTutorName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentDetails?.id]);

  const openNotificationWithIcon = (
    type: NotificationType,
    action: keyof typeof ActionForNotification,
    description?: string,
  ) => {
    NotificationInstanceApi[type]({
      message: popupTitleAndMessage[action][type].title,
      description: description ?? popupTitleAndMessage[action][type].description,
      placement: "bottomRight",
    });
  };

  async function fetchPaymentDetails() {
    try {
      axiosInstance
        .get(
          `/v1/cre/user/payment-details/${studentDetails?.studentId}`,
          // `/v1/cre/user/payment-details/1456560?${studentDetails?.studentId}`,
          {
            params: { dealId: studentDetails?.id },
          },
        )
        .then((response) => {
          const { data, is_success } = response.data;

          if (is_success) {
            setPaymentDetails(data);
          }
        })
        .catch(() => { });
    } catch { }
  }

  async function openTransferPaymentModal(order: StudentPaymentDetailType["orders"][0]) {
    setSelectedPayment(order);
    setIsTransferModalOpen(true);
    setTransferStep("loading");
    setTransferLoading(true);

    try {
      const response = await axiosInstance.get(`/v1/cre/student/recommendation/process`, {
        params: {
          dealings_id: studentDetails?.id,
          page: 1,
          limit: 25,
        },
      });

      const processTutors = response?.data?.data?.data || [];
      const confirmedTutor = processTutors.find((item: any) => item?.process_status === "CONFIRMED");
      const fallbackTutor = processTutors[0];
      const tutorId = confirmedTutor?.tutor_id || fallbackTutor?.tutor_id;

      if (tutorId) {
        // Fetch tutor details
        const tutorResponse = await axiosInstance.get(`/v1/cre/tutor/details-for-transfer`, {
          params: { tutor_id: tutorId },
        });

        // Check linked account status
        const linkedAccountResponse = await axiosInstance.get(`/v1/cre/tutor/linked-account`, {
          params: { tutor_id: tutorId },
        });

        if (tutorResponse.data.is_success) {
          const tutorData = tutorResponse.data.data;
          setTransferTutorData(tutorData);
          const linkedData = linkedAccountResponse.data.data;
          // Use linked-account API data, fallback to details-for-transfer if API returns empty
          setRazorpayLinkedAccountId(linkedData?.accountId || tutorData?.razorpay_linked_account_id || null);
          setRazorpayAccountStatus(linkedData?.status || tutorData?.razorpay_account_status || null);
          setBankDetailsDraft({
            accountHolderName: tutorData?.bank_details?.accountHolderName || "",
            bankName: tutorData?.bank_details?.bankName || "",
            accountNumber: tutorData?.bank_details?.accountNumber || "",
            ifscCode: tutorData?.bank_details?.ifscCode || "",
            panNumber: tutorData?.bank_details?.panNumber || "",
            upiId: tutorData?.bank_details?.upiId || "",
          });
        }
      }
    } catch {
      notification.error({ message: "Failed to load tutor details" });
    } finally {
      setTransferLoading(false);
      setTransferStep("form");
    }
  }

  const handleCreateRazorpayAccount = async () => {
    if (!transferTutorData) return;
    setCreatingAccount(true);
    try {
      const response = await axiosInstance.post(`/v1/cre/tutor/create-linked-account`, {
        tutor_id: transferTutorData.tutor_id,
      });
      if (response.data.is_success) {
        const accountId = response.data.data?.id;
        const status = response.data.data?.status;
        setRazorpayLinkedAccountId(accountId);
        setRazorpayAccountStatus(status);
        notification.success({ message: "Razorpay Linked Account Created Successfully!" });
      } else {
        notification.error({ message: getApiErrorMessage(response.data, "Failed to create Razorpay account") });
      }
    } catch (error: any) {
      notification.error({ message: getApiErrorMessage(error?.response?.data, "Failed to create Razorpay account") });
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleSaveBankDetails = async () => {
    if (!transferTutorData) return;
    const { accountHolderName, accountNumber, ifscCode } = bankDetailsDraft;
    if (!accountHolderName || !accountNumber || !ifscCode) {
      notification.error({ message: "Account Holder Name, Account Number, and IFSC Code are required" });
      return;
    }
    setSavingBankDetails(true);
    try {
      const response = await axiosInstance.put(`/v1/cre/tutor/${transferTutorData.tutor_id}/bank-details`, bankDetailsDraft);
      if (response.data.is_success) {
        setTransferTutorData((prev) => (prev ? { ...prev, bank_details: response.data.data } : prev));
        setIsEditingBankDetails(false);
        notification.success({ message: "Bank details saved successfully!" });
      } else {
        notification.error({ message: getApiErrorMessage(response.data, "Failed to save bank details") });
      }
    } catch (error: any) {
      notification.error({ message: getApiErrorMessage(error?.response?.data, "Failed to save bank details") });
    } finally {
      setSavingBankDetails(false);
    }
  };

  const handleRaiseTransferRequest = async () => {
    if (!transferTutorData || !selectedPayment) return;
    if (amountValue <= 0) {
      notification.error({ message: "Please enter a valid amount" });
      return;
    }
    if (amountType === "percentage" && amountValue > 100) {
      notification.error({ message: "Percentage cannot be greater than 100" });
      return;
    }
    if (calculatedAmount <= 0) {
      notification.error({ message: "Calculated amount must be greater than 0" });
      return;
    }
    setRaisingRequest(true);
    try {
      const response = await axiosInstance.post(`/v1/cre/tutor/transfer-request`, {
        tutor_id: transferTutorData.tutor_id,
        payment_id: selectedPayment.id,
        deal_id: studentDetails?.id,
        amount_type: amountType,
        amount_value: amountValue,
        calculated_amount: calculatedAmount,
        notes,
        razorpay_linked_account_id: razorpayLinkedAccountId,
        transfer_type: "RAZORPAY",
      });
      if (response.data.is_success) {
        notification.success({ message: "Transfer Request Raised Successfully!" });
        setIsTransferModalOpen(false);
        resetTransferForm();
      } else {
        notification.error({ message: getApiErrorMessage(response.data, "Failed to raise request") });
      }
    } catch (error: any) {
      notification.error({ message: getApiErrorMessage(error?.response?.data, "Failed to raise transfer request") });
    } finally {
      setRaisingRequest(false);
    }
  };

  const handleRaiseManualTransferRequest = async () => {
    if (!transferTutorData || !selectedPayment || !manualMode || !manualRef) return;
    if (calculatedAmount <= 0) {
      notification.error({ message: "Please enter a valid transfer amount" });
      return;
    }
    setRaisingRequest(true);
    try {
      const response = await axiosInstance.post(`/v1/cre/tutor/transfer-request`, {
        tutor_id: transferTutorData.tutor_id,
        payment_id: selectedPayment.id,
        deal_id: studentDetails?.id,
        amount_type: "fixed",
        amount_value: calculatedAmount,
        calculated_amount: calculatedAmount,
        notes: manualNotes || null,
        transfer_type: "MANUAL",
      });
      if (response.data.is_success) {
        notification.success({ message: "Manual Transfer Request Raised Successfully!" });
        setIsTransferModalOpen(false);
        resetTransferForm();
      } else {
        notification.error({ message: getApiErrorMessage(response.data, "Failed to raise request") });
      }
    } catch (error: any) {
      notification.error({ message: getApiErrorMessage(error?.response?.data, "Failed to raise transfer request") });
    } finally {
      setRaisingRequest(false);
    }
  };

  const resetTransferForm = () => {
    setSelectedPayment(null);
    setAmountType("percentage");
    setAmountValue(0);
    setCalculatedAmount(0);
    setNotes("");
    setRazorpayLinkedAccountId(null);
    setRazorpayAccountStatus(null);
    setTransferTab("razorpay");
    setManualMode("");
    setManualRef("");
    setManualDate(dayjs().format("YYYY-MM-DD"));
    setManualNotes("");
    setTransferTutorData(null);
    setTransferStep("loading");
    setIsEditingBankDetails(false);
    setBankDetailsDraft({ accountHolderName: "", bankName: "", accountNumber: "", ifscCode: "", panNumber: "", upiId: "" });
  };

  const handleAmountChange = (value: number) => {
    const clampedValue = amountType === "percentage" && value > 100 ? 100 : value;
    setAmountValue(clampedValue);
    if (selectedPayment && clampedValue > 0) {
      if (amountType === "percentage") {
        setCalculatedAmount(Math.round(((selectedPayment.billing_amount || selectedPayment.custom_amount) * clampedValue) / 100));
      } else {
        setCalculatedAmount(clampedValue);
      }
    }
  };

  const handleAmountTypeChange = (type: "percentage" | "fixed") => {
    setAmountType(type);
    setAmountValue(0);
    setCalculatedAmount(0);
  };

  async function fetchOngoingTutorName() {
    try {
      const response = await axiosInstance.get(`/v1/cre/student/recommendation/process`, {
        params: {
          dealings_id: studentDetails?.id,
          page: 1,
          limit: 25,
        },
      });

      const processTutors = response?.data?.data?.data || [];
      const confirmedTutor = processTutors.find((item: any) => item?.process_status === "CONFIRMED");
      const fallbackTutor = processTutors[0];
      setOngoingTutorName(confirmedTutor?.tutor_name || fallbackTutor?.tutor_name || "");
    } catch (error) {
      setOngoingTutorName("");
    }
  }

  async function emailCutomAmountPayment(values: { orderId: string }) {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/v1/cre/student/payment-order-invoice/${studentDetails?.studentId}`, {
        ...values,
        dealId: studentDetails?.id,
      });

      const { is_success, error } = response.data;

      if (is_success) {
        openNotificationWithIcon("success", "STUDENT_PAYMENT_ORDER_EMAIL");
        fetchPaymentDetails();
      } else {
        const errorMessage = error?.message || "Something went wrong!";
        openNotificationWithIcon("error", "STUDENT_PAYMENT_ORDER_EMAIL", errorMessage);
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.error?.message, error?.response?.data?.message, error?.message);
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      openNotificationWithIcon("error", "STUDENT_PAYMENT_ORDER_EMAIL", errorMessage);
    } finally {
      setLoading(false);
      setCustomAmountRequest(false);
    }
  }

  async function sendPaymentRecipt(paymentId: string) {
    try {
      axiosInstance
        .post(`/v1/cre/student/payment-reciept`, {
          paymentId,
        })
        .then((response) => {
          const { is_success } = response.data;
          if (is_success) {
            openNotificationWithIcon("success", "STUDENT_PAYMENT_RECEIPT");
          } else {
            openNotificationWithIcon(
              "error",
              "STUDENT_PAYMENT_RECEIPT",
              response.data?.error?.message || "Something went wrong",
            );
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.error?.message || error?.response?.statusText || error?.message || "Unknown error";
          openNotificationWithIcon("error", "STUDENT_PAYMENT_RECEIPT", message);
        });
    } catch (error: any) {
      openNotificationWithIcon("error", "STUDENT_PAYMENT_RECEIPT", error?.message || "Unexpected error");
    }
  }

  async function sendemailInitialSubcriptionPayment(paymentType: "basic" | "premium") {
    try {
      axiosInstance
        .get(`/v1/cre/student/payment-subscription-invoice/${studentDetails?.studentId}`, {
          params: {
            dealId: studentDetails?.id,
            selectedPlan: "plan_O00JuKH0wFciMT",
            paymentType: paymentType,
          },
        })
        .then(() => {
          openNotificationWithIcon("success", "STUDENT_PAYMENT_SEND_LINK");
          fetchPaymentDetails();
          setIsPaymentTypeModalOpen(false);
        })
        // .catch((error) => {
        //   openNotificationWithIcon("error", "STUDENT_PAYMENT_SEND_LINK", error?.resposne ?? error);
        // });
        .catch((error) => {
          const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";

          openNotificationWithIcon("error", "STUDENT_PAYMENT_SEND_LINK", errorMessage);
        });
    } catch (error) { }
  }

  const handlePaymentTypeProceed = () => {
    sendemailInitialSubcriptionPayment(selectedPaymentType);
  };
  // ✅ Precompute sorted orders by order_id
  const sortedOrders = useMemo(() => {
    return [...(paymentDetails?.orders || [])].sort((a, b) => Number(a.order_id) - Number(b.order_id));
  }, [paymentDetails]);

  // ✅ Map order_id -> nth payment
  const paymentIndexMap: Record<string, number> = {};
  sortedOrders.forEach((order, index) => {
    paymentIndexMap[order.order_id] = index + 1;
  });

  const handleDateChange = async (
    Id: number,
    paymentId: string,
    order_id: string,
    field: "payment_date" | "due_date" | "next_due_date",
    value: Dayjs | null,
  ) => {
    try {
      const formattedDate = value ? value.toISOString() : null;

      // ✅ Await only
      const response = await axiosInstance.put(`v1/cre/student/payment/${Id}/update-date`, {
        [field]: formattedDate,
        paymentId,
        order_id,
      });

      if (response.data.success) {
        fetchPaymentDetails();
        notification.success({
          message: "Success",
          description: `${field.replace("_", " ")} updated successfully.`,
        });
      } else {
        notification.error({
          message: "Error",
          description: response.data.message || "Failed to update date.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update date. Please try again.",
      });
    }
  };

  const deletePayment = async (Id: number) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`v1/cre/student/payment/${Id}`);

      if (response.data.success) {
        notification.success({
          message: "Success",
          description: "Payment deleted successfully.",
        });
        fetchPaymentDetails();
      } else {
        notification.error({
          message: "Error",
          description: response.data.message || "Failed to delete payment.",
        });
      }
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to delete payment.",
      });
    } finally {
      setLoading(false);
    }
  };

  const startTransferEdit = (order: StudentPaymentDetailType["orders"][0]) => {
    setEditingTransferRowId(order.id);
    setTransferDraft({
      tutor_transferred: Boolean(order.tutor_transferred),
      transfer_date: order.transfer_date ? dayjs(order.transfer_date) : dayjs(),
    });
  };

  const resetTransferEdit = (order: StudentPaymentDetailType["orders"][0]) => {
    setTransferDraft({
      tutor_transferred: Boolean(order.tutor_transferred),
      transfer_date: order.transfer_date ? dayjs(order.transfer_date) : null,
    });
    setEditingTransferRowId(null);
  };

  const saveTransferEdit = (order: StudentPaymentDetailType["orders"][0]) => {
    const updatedOrders = (paymentDetails?.orders || []).map((item) => {
      if (item.id !== order.id) return item;
      return {
        ...item,
        tutor_transferred: transferDraft.tutor_transferred ? 1 : 0,
        transfer_date: transferDraft.transfer_date ? transferDraft.transfer_date.toISOString() : null,
      };
    });

    setPaymentDetails((prev) => ({
      ...prev,
      orders: updatedOrders,
    }));
    setEditingTransferRowId(null);
    notification.success({
      message: "Saved",
      description: "Transfer fields updated in UI.",
    });
  };

  return (
    <>
      {paymentDetails && (
        <div>
          {NotificationInstanceContextHolder}
          {(() => {
            const subscriptionStatus = paymentDetails?.subscriptionData?.subscription_status;
            const paymentStatusCode = paymentDetails?.subscriptionData?.status_code;

            const isSuccess = subscriptionStatus
              ? (subscriptionStatus === "ACTIVE" || subscriptionStatus === "COMPLETED")
              : (paymentStatusCode === "SUCCESS");

            let statusMessage = "";
            let alertType: "success" | "warning" | "error" | "info" = "info";

            if (subscriptionStatus) {
              if (subscriptionStatus === "ACTIVE") {
                statusMessage = "Subscription Status: Active";
                alertType = "success";
              } else if (subscriptionStatus === "COMPLETED") {
                statusMessage = "Subscription Status: Completed";
                alertType = "success";
              } else if (subscriptionStatus === "EXPIRED") {
                statusMessage = "Subscription Status: Expired";
                alertType = "error";
              } else if (subscriptionStatus === "CANCELLED") {
                statusMessage = "Subscription Status: Cancelled";
                alertType = "error";
              } else {
                statusMessage = `Subscription Status: ${subscriptionStatus.charAt(0) + subscriptionStatus.slice(1).toLowerCase()} - Email sent count: ${paymentDetails?.subscriptionData?.invoice_sent_count || 0}`;
                alertType = "error";
              }
            } else {
              if (paymentStatusCode === "SUCCESS") {
                statusMessage = "Subscription Status: Success";
                alertType = "success";
              } else {
                statusMessage = `Subscription Status: Pending - Email sent count: ${paymentDetails?.subscriptionData?.invoice_sent_count || 0}`;
                alertType = "error";
              }
            }

            return (
              <Alert
                style={{ margin: "20px 0" }}
                message={statusMessage}
                type={alertType}
                showIcon
                action={
                  isSuccess ? (
                    <Button
                      type="primary"
                      onClick={() => {
                        sendPaymentRecipt(paymentDetails?.subscriptionData?.payment_id as string);
                      }}
                    >
                      Send Reciept
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={() => {
                        setIsPaymentTypeModalOpen(true);
                      }}
                    >
                      Email Payment Link
                    </Button>
                  )
                }
              />
            );
          })()}
          <Table<StudentPaymentDetailType["orders"][0]>
            rowKey="order_id"
            dataSource={paymentDetails?.orders}
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            }}
            scroll={{ x: "max-content" }}
            columns={[
              {
                title: "Order ID",
                dataIndex: "order_id",
                width: 90,
                sorter: (a, b) => Number(b.order_id) - Number(a.order_id),
                defaultSortOrder: "ascend",
              },
              {
                title: "Created At",
                dataIndex: "created_at",
                width: 120,
                render: (date: string) => date?.split("T")[0],
              },
              {
                title: "Updated At",
                dataIndex: "updated_at",
                width: 120,
                render: (date: string) => date?.split("T")[0],
              },
              {
                title: "Payment",
                width: 120,
                render: (_: any, order: StudentPaymentDetailType["orders"][0]) => {
                  const index = paymentIndexMap[order.order_id];
                  return `${index}${index === 1 ? "st" : index === 2 ? "nd" : index === 3 ? "rd" : "th"} Payment`;
                },
              },
              {
                title: "Amount",
                dataIndex: "billing_amount",
                width: 120,
                render: (amount: number) => <span>{amount} Rs/-</span>,
              },
              {
                title: "GST By User",
                dataIndex: "gst_by_user",
                width: 110,
                render: (gst: number) => (gst ? "Yes" : "No"),
              },
              {
                title: "Total Amount",
                dataIndex: "custom_amount",
                width: 120,
                render: (amount: number) => <span>{amount} Rs/-</span>,
              },
              {
                title: "Payment Status",
                dataIndex: "status_code",
                width: 120,
                render: (status: string) => (
                  <Tag
                    color={
                      status === "SUCCESS"
                        ? "#CFFFB9" // green
                        : "#FFF7CA" // blue
                    }
                    style={{
                      color: status === "SUCCESS" ? "#22A50D" : "#E0BC01",
                    }}
                  >
                    {status === "SUCCESS" ? "Received" : "Payment Pending"}
                  </Tag>
                ),
              },
              {
                title: "Due Date",
                dataIndex: "due_date",
                width: 200,
                render: (date: string | null, order: StudentPaymentDetailType["orders"][0]) =>
                  isEditable ? (
                    <DatePicker
                      showTime
                      allowClear={false}
                      value={date ? dayjs(date) : null}
                      format="YYYY-MM-DD HH:mm"
                      onChange={debounce(
                        (val: Dayjs | null) =>
                          handleDateChange(order.id, order.payment_id, order.order_id, "due_date", val),
                        300,
                      )}
                    />
                  ) : date ? (
                    dayjs(date).format("YYYY-MM-DD HH:mm")
                  ) : (
                    "-"
                  ),
              },
              {
                title: "Payment Date",
                dataIndex: "payment_date",
                width: 200,
                render: (date: string | null, order: StudentPaymentDetailType["orders"][0]) =>
                  isPaymentEditable ? (
                    <DatePicker
                      showTime
                      allowClear={false}
                      value={date ? dayjs(date) : null}
                      format="YYYY-MM-DD HH:mm"
                      // ✅ debounce API call to avoid double firing
                      onChange={debounce(
                        (val: Dayjs | null) =>
                          handleDateChange(order.id, order.payment_id, order.order_id, "payment_date", val),
                        300, // wait 300ms to prevent double calls
                      )}
                    />
                  ) : date ? (
                    dayjs(date).format("YYYY-MM-DD HH:mm")
                  ) : (
                    "-"
                  ),
              },

              {
                title: "Next Due Date",
                dataIndex: "next_due_date",
                width: 200,
                render: (date: string | null, order: StudentPaymentDetailType["orders"][0]) =>
                  isEditable ? (
                    <DatePicker
                      showTime
                      allowClear={false}
                      value={date ? dayjs(date) : null}
                      format="YYYY-MM-DD HH:mm"
                      onChange={debounce(
                        (val: Dayjs | null) =>
                          handleDateChange(order.id, order.payment_id, order.order_id, "next_due_date", val),
                        300,
                      )}
                    />
                  ) : date ? (
                    dayjs(date).format("YYYY-MM-DD HH:mm")
                  ) : (
                    "-"
                  ),
              },
              {
                title: "Tutor Name",
                dataIndex: "tutor_name",
                width: 180,
                render: (value: string | null) => value || ongoingTutorName || "-",
              },
              {
                title: "Tutor Transferred",
                dataIndex: "tutor_transferred",
                width: 160,
                render: (value: number | boolean | null, order: StudentPaymentDetailType["orders"][0]) =>
                  editingTransferRowId === order.id ? (
                    <Checkbox
                      checked={transferDraft.tutor_transferred}
                      onChange={(e) => {
                        setTransferDraft((prev) => ({ ...prev, tutor_transferred: e.target.checked }));
                      }}
                    />
                  ) : value ? (
                    "Yes"
                  ) : (
                    "No"
                  ),
              },
              {
                title: "Transfer date",
                dataIndex: "transfer_date",
                width: 190,
                render: (value: string | null, order: StudentPaymentDetailType["orders"][0]) =>
                  editingTransferRowId === order.id ? (
                    <DatePicker
                      showTime
                      allowClear
                      value={transferDraft.transfer_date}
                      format="YYYY-MM-DD HH:mm"
                      onChange={(val) => {
                        setTransferDraft((prev) => ({ ...prev, transfer_date: val }));
                      }}
                    />
                  ) : value ? (
                    dayjs(value).format("YYYY-MM-DD HH:mm")
                  ) : (
                    "-"
                  ),
              },
              {
                title: "",
                width: 120,
                align: "center",
                render: (_: any, order: StudentPaymentDetailType["orders"][0]) =>
                  !isEditable ? (
                    "-"
                  ) : editingTransferRowId === order.id ? (
                    <Space>
                      <Tooltip title="Save">
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          loading={loading}
                          onClick={() => saveTransferEdit(order)}
                        />
                      </Tooltip>
                      <Tooltip title="Reset">
                        <Button icon={<RedoOutlined />} onClick={() => resetTransferEdit(order)} />
                      </Tooltip>
                    </Space>
                  ) : (
                    <Tooltip title="Edit">
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => startTransferEdit(order)}
                        disabled={editingTransferRowId !== null && editingTransferRowId !== order.id}
                      />
                    </Tooltip>
                  ),
              },
              {
                title: "Actions",
                width: 280,
                render: (_: any, order: StudentPaymentDetailType["orders"][0]) => (
                  <Space>
                    <Button
                      type="primary"
                      onClick={() => {
                        if (order.status_code === "SUCCESS") {
                          sendPaymentRecipt(order.payment_id);
                        } else {
                          emailCutomAmountPayment({ orderId: order.order_id });
                        }
                      }}
                    >
                      {order.status_code === "SUCCESS" ? "Send Receipt" : "Send Payment Link"}
                    </Button>
                    {(role === USER_ROLES.SUPERADMIN || isTransferPayment) && (
                      <Button
                        onClick={() => openTransferPaymentModal(order)}
                        style={{ marginLeft: 4 }}
                      >
                        Transfer Payment
                      </Button>
                    )}
                  </Space>
                ),
              },

              // {
              //   title: "Remainder",
              //   render: () => (
              //     <Button
              //       onClick={() => {
              //         notification.info({
              //           message: "Remainder Clicked",
              //         });
              //       }}
              //       style={{
              //         backgroundColor: "#FF0000", // light red background
              //         color: "#fff", // red text color
              //         // padding: "2px 8px",
              //         borderRadius: "8px",
              //       }}
              //     >
              //       Send Remainder
              //       <BellOutlined style={{ marginLeft: "4px" }} />
              //     </Button>
              //   ),
              // },
              {
                title: "Request Count",
                dataIndex: "invoice_sent_count",
                align: "center",
                width: 120,
              },
              {
                title: "Receipt",
                align: "center",
                width: 120,
                render: (order: StudentPaymentDetailType["orders"][0]) =>
                  order.invoice_url !== null ? (
                    <Button
                      type="link"
                      onClick={() => {
                        window.open(order.invoice_url, "_blank");
                      }}
                    >
                      View Receipt
                    </Button>
                  ) : (
                    "-"
                  ),
              },
              {
                title: "Payment Link",
                dataIndex: "payment_link",
                align: "center",
                width: 150,
                ellipsis: true,
                render: (link: string | null) =>
                  link ? (
                    <a href={link} target="_blank" rel="noreferrer">
                      Link <LinkOutlined />
                    </a>
                  ) : (
                    "-"
                  ),
              },
              {
                title: "Comments",
                width: 120,
                render: (order: StudentPaymentDetailType["orders"][0]) => (
                  <PaymentCommentsPopup
                    cellValue={order.comment_count}
                    data={{
                      payment_id: order.id,
                      comment_count: order.comment_count,
                    }}
                  />
                ),
              },
              {
                title: "Delete",
                width: 80,
                align: "center",
                render: (_: any, order: StudentPaymentDetailType["orders"][0]) => {
                  return (
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      loading={loading}
                      disabled={!(order.status_code === "CREATED" || order.status_code === "payment pending")}
                      onClick={() => {
                        Modal.confirm({
                          title: "Delete Payment",
                          content: "Are you sure you want to delete this payment and its associated order?",
                          okText: "Yes",
                          okType: "danger",
                          cancelText: "No",
                          onOk: () => deletePayment(order.id),
                        });
                      }}
                    />
                  );
                },
              },
            ]}
          />
        </div>
      )}
      <Flex align="center" justify="center" style={{ marginTop: "20px" }}>
        <Button
          type="primary"
          onClick={() => {
            setCustomAmountRequest(true);
          }}
        >
          Add Payment
        </Button>
      </Flex>
      {customAmountRequest && (
        <Drawer
          title="Add Payment"
          onClose={() => {
            setCustomAmountRequest(false);
          }}
          open={customAmountRequest}
          width={"40vw"}
        >
          <Form
            layout="vertical"
            onFinish={(values) => {
              emailCutomAmountPayment(values);
            }}
          >
            <Form.Item
              label="Amount"
              name="amount"
              rules={[
                {
                  required: true,
                  message: "Please enter the amount",
                },
              ]}
            >
              <Input
                type="number"
                placeholder="Rs"
                onChange={(e) => {
                  const value = e.target.value;
                  const filteredValue = value.replace(/[^0-9]/g, "");
                  e.target.value = filteredValue;
                }}
              />
            </Form.Item>
            {/* <Form.Item
                    label="Description"
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a description",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item> */}
            {/* <Form.Item
              label="Payment Type"
              name="paymentType"
              rules={[
                { required: true, message: "Please select a payment type" },
              ]}
            >
              <Radio.Group style={{ display: "flex", gap: "20px" }}>
                <Radio value="initial">Initial Payment</Radio>
                <Radio value="first_month">First Month Payment</Radio>
                <Radio value="recurring">Recurring Payment</Radio>
              </Radio.Group>
            </Form.Item> */}
            <Form.Item label="Tax Paid" name="gstByUser" valuePropName="checked" initialValue={true}>
              <Checkbox />
            </Form.Item>
            <Form.Item>
              <Space style={{ width: "100%" }}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Submit
                </Button>
                <Button
                  onClick={() => {
                    setCustomAmountRequest(false);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Drawer>
      )}

      {/* Payment Type Selection Modal */}
      <Modal
        title="Select Parents Type"
        open={isPaymentTypeModalOpen}
        onOk={handlePaymentTypeProceed}
        onCancel={() => {
          setIsPaymentTypeModalOpen(false);
          setSelectedPaymentType("basic");
        }}
        okText="Proceed"
        cancelText="Cancel"
      >
        <div style={{ padding: "20px 0" }}>
          <Radio.Group
            onChange={(e) => setSelectedPaymentType(e.target.value)}
            value={selectedPaymentType}
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <Radio value="basic" style={{ fontSize: "16px" }}>
              Normal Parents
            </Radio>
            <Radio value="premium" style={{ fontSize: "16px" }}>
              Shadow Parents
            </Radio>
          </Radio.Group>
        </div>
      </Modal>

      {/* Transfer Payment Modal */}
      <Modal
        title="Transfer Payment"
        open={isTransferModalOpen}
        onCancel={() => {
          setIsTransferModalOpen(false);
          resetTransferForm();
        }}
        footer={null}
        width={600}
      >
        <Spin spinning={transferLoading} tip="Loading...">
          {transferTutorData && transferStep === "form" ? (
            <div>
              {/* Section 1: Bank Details — required before creating Razorpay account */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Bank Details</h3>
                {isEditingBankDetails ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: "#888" }}>Account Holder Name *</label>
                      <Input
                        placeholder="Enter holder name"
                        value={bankDetailsDraft.accountHolderName}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, accountHolderName: e.target.value }))}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#888" }}>Bank Name</label>
                      <Input
                        placeholder="Enter bank name"
                        value={bankDetailsDraft.bankName}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, bankName: e.target.value }))}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#888" }}>Account Number *</label>
                      <Input
                        placeholder="Enter account number"
                        value={bankDetailsDraft.accountNumber}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, accountNumber: e.target.value }))}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#888" }}>IFSC Code *</label>
                      <Input
                        placeholder="e.g. SBIN0001234"
                        value={bankDetailsDraft.ifscCode}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, ifscCode: e.target.value }))}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#888" }}>PAN Number</label>
                      <Input
                        placeholder="Enter PAN"
                        value={bankDetailsDraft.panNumber}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, panNumber: e.target.value }))}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#888" }}>UPI ID</label>
                      <Input
                        placeholder="e.g. name@upi"
                        value={bankDetailsDraft.upiId}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, upiId: e.target.value }))}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, marginTop: 4 }}>
                      <Button type="primary" onClick={handleSaveBankDetails} loading={savingBankDetails}>
                        Save Bank Details
                      </Button>
                      <Button
                        onClick={() => {
                          setIsEditingBankDetails(false);
                          setBankDetailsDraft({
                            accountHolderName: transferTutorData?.bank_details?.accountHolderName || "",
                            bankName: transferTutorData?.bank_details?.bankName || "",
                            accountNumber: transferTutorData?.bank_details?.accountNumber || "",
                            ifscCode: transferTutorData?.bank_details?.ifscCode || "",
                            panNumber: transferTutorData?.bank_details?.panNumber || "",
                            upiId: transferTutorData?.bank_details?.upiId || "",
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {transferTutorData?.bank_details && (
                      Object.keys(transferTutorData.bank_details).filter((k) => !(k.startsWith("razorpay_")) && k !== "address" && (transferTutorData.bank_details as any)[k]).length > 0
                    ) ? (
                      <div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                          <div>
                            <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Bank</p>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.bank_details.bankName || "-"}</p>
                          </div>
                          <div>
                            <p style={{ margin: 0, color: "#888", fontSize: 12 }}>A/C Number</p>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.bank_details.accountNumber ? "****" + transferTutorData.bank_details.accountNumber.slice(-4) : "-"}</p>
                          </div>
                          <div>
                            <p style={{ margin: 0, color: "#888", fontSize: 12 }}>IFSC</p>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.bank_details.ifscCode || "-"}</p>
                          </div>
                          <div>
                            <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Holder Name</p>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.bank_details.accountHolderName || "-"}</p>
                          </div>
                          <div>
                            <p style={{ margin: 0, color: "#888", fontSize: 12 }}>PAN</p>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.bank_details.panNumber || "-"}</p>
                          </div>
                          <div>
                            <p style={{ margin: 0, color: "#888", fontSize: 12 }}>UPI</p>
                            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.bank_details.upiId || "-"}</p>
                          </div>
                        </div>
                        <Button icon={<EditOutlined />} onClick={() => setIsEditingBankDetails(true)} size="small">
                          Edit Bank Details
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Alert
                          message="No bank details found for this tutor"
                          description="Please add bank details before creating a Razorpay account."
                          type="warning"
                          showIcon
                          style={{ marginBottom: 8 }}
                        />
                        <Button type="primary" icon={<EditOutlined />} onClick={() => setIsEditingBankDetails(true)}>
                          Add Bank Details
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Section 2: Razorpay Account Status */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Razorpay Account</h3>
                {razorpayLinkedAccountId ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Tag color={razorpayAccountStatus === "active" ? "green" : "orange"} style={{ fontSize: 12 }}>
                        {razorpayAccountStatus || "created"}
                      </Tag>
                      <span style={{ fontSize: 12, color: "#888" }}>ID: {razorpayLinkedAccountId}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Alert
                      message="No Razorpay linked account found"
                      description={
                        transferTutorData?.bank_details?.accountHolderName && transferTutorData?.bank_details?.accountNumber && transferTutorData?.bank_details?.ifscCode
                          ? "Create a Razorpay linked account to enable transfers."
                          : "Add bank details first to create a Razorpay linked account."
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                    <Button
                      type="primary"
                      onClick={handleCreateRazorpayAccount}
                      loading={creatingAccount}
                      disabled={
                        !transferTutorData?.bank_details?.accountHolderName ||
                        !transferTutorData?.bank_details?.accountNumber ||
                        !transferTutorData?.bank_details?.ifscCode
                      }
                    >
                      Create Razorpay Account
                    </Button>
                  </div>
                )}
              </div>

              {/* Section 3: Tutor Basic Details */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Tutor Details</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Name</p>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.fullname || "-"}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Phone</p>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.phone || "-"}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: "#888", fontSize: 12 }}>Tutor ID</p>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{transferTutorData.tutor_id || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Section 4: Transfer Amount Form */}
              {selectedPayment && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>Transfer Amount</h3>

                  <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <p style={{ margin: 0, fontSize: 13 }}>
                      Payment: <strong>{selectedPayment.order_id}</strong> —{" "}
                      <strong>₹{selectedPayment.billing_amount || selectedPayment.custom_amount}</strong>
                    </p>
                  </div>

                  {/* Tab Switcher */}
                  <Flex gap={8} style={{ marginBottom: 12 }}>
                    <Button
                      type={transferTab === "razorpay" ? "primary" : "default"}
                      onClick={() => setTransferTab("razorpay")}
                      style={{ flex: 1 }}
                    >
                      Via Razorpay
                    </Button>
                    <Button
                      type={transferTab === "manual" ? "primary" : "default"}
                      onClick={() => setTransferTab("manual")}
                      style={{ flex: 1 }}
                    >
                      Manual
                    </Button>
                  </Flex>

                  {transferTab === "razorpay" ? (
                    <>
                      {!razorpayLinkedAccountId ? (
                        <div style={{ textAlign: "center", padding: "24px 16px", background: "#fafafa", borderRadius: 8, border: "1px solid #d9d9d9" }}>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
                          <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#333" }}>Razorpay Account Not Found</p>
                          <p style={{ margin: "0 0 16px", color: "#666", fontSize: 13 }}>
                            You need a Razorpay linked account to raise a Razorpay transfer.
                          </p>
                          <Button type="primary" onClick={() => setTransferTab("manual")}>
                            Switch to Manual Tab
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                            <Button
                              type={amountType === "percentage" ? "primary" : "default"}
                              onClick={() => handleAmountTypeChange("percentage")}
                              style={{ flex: 1 }}
                            >
                              Percentage (%)
                            </Button>
                            <Button
                              type={amountType === "fixed" ? "primary" : "default"}
                              onClick={() => handleAmountTypeChange("fixed")}
                              style={{ flex: 1 }}
                            >
                              Fixed Amount (₹)
                            </Button>
                          </div>

                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
                              {amountType === "percentage" ? "Percentage (%)" : "Fixed Amount (₹)"}
                            </label>
                            <Input
                              type="number"
                              placeholder={amountType === "percentage" ? "e.g. 80" : "e.g. 4000"}
                              value={amountValue || ""}
                              onChange={(e) => handleAmountChange(Number(e.target.value))}
                              min={0}
                              max={amountType === "percentage" ? 100 : undefined}
                              style={{ width: "100%" }}
                            />
                          </div>

                          {calculatedAmount > 0 && (
                            <Alert
                              message={`Transfer Amount: ₹${calculatedAmount}`}
                              description={
                                amountType === "percentage"
                                  ? `(${amountValue}% of ₹${selectedPayment.billing_amount || selectedPayment.custom_amount})`
                                  : "Fixed amount"
                              }
                              type="info"
                              showIcon
                              style={{ marginBottom: 12 }}
                            />
                          )}

                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Notes (optional)</label>
                            <Input.TextArea
                              rows={2}
                              placeholder="Add any notes..."
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                            />
                          </div>

                          <Button
                            type="primary"
                            onClick={handleRaiseTransferRequest}
                            loading={raisingRequest}
                            disabled={calculatedAmount <= 0}
                            block
                          >
                            Raise Transfer Request
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Alert
                        message="Manual transfer — no Razorpay account needed. Fill details to record the transfer."
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                      />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Transfer Mode *</label>
                          <Select
                            style={{ width: "100%" }}
                            value={manualMode || undefined}
                            onChange={(val) => setManualMode(val)}
                            placeholder="Select mode"
                          >
                            <Select.Option value="BANK_TRANSFER">Bank Transfer (NEFT/RTGS/IMPS)</Select.Option>
                            <Select.Option value="UPI">UPI</Select.Option>
                            <Select.Option value="CASH">Cash</Select.Option>
                          </Select>
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Calculated Amount (₹) *</label>
                          <Input
                            type="number"
                            placeholder="e.g. 1500"
                            value={calculatedAmount || ""}
                            onChange={(e) => setCalculatedAmount(Number(e.target.value))}
                            style={{ width: "100%" }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Transaction Reference *</label>
                          <Input
                            placeholder="UTRN / Transaction ID / Ref"
                            value={manualRef}
                            onChange={(e) => setManualRef(e.target.value)}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Transfer Date</label>
                          <DatePicker
                            style={{ width: "100%" }}
                            value={manualDate ? dayjs(manualDate) : undefined}
                            onChange={(date) => setManualDate(date?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"))}
                            format="DD MMM YYYY"
                          />
                        </div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Notes (optional)</label>
                        <Input.TextArea
                          rows={2}
                          placeholder="Add any notes..."
                          value={manualNotes}
                          onChange={(e) => setManualNotes(e.target.value)}
                        />
                      </div>
                      <Button
                        type="primary"
                        loading={raisingRequest}
                        disabled={!manualMode || !manualRef || calculatedAmount <= 0}
                        onClick={handleRaiseManualTransferRequest}
                        block
                      >
                        Raise Manual Transfer Request
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            !transferLoading && (
              <div style={{ textAlign: "center", padding: 20 }}>
                <p style={{ color: "#888" }}>No tutor data found</p>
              </div>
            )
          )}
        </Spin>
      </Modal>
    </>
  );
}
